import { animate, style, transition, trigger } from '@angular/animations'
import {
  BirthControlMethodBreakpointOption,
  BreakpointActions,
  ConsideringPurchaseOption,
  EligibleAppleWatchBreakpointOption,
  Goal,
  GoalBreakpointOption,
  HormonalBirthControlHowLongBreakpointOption,
  HormonalBirthControlUsageBreakpointOption,
  PurchaseNewWatchOption,
  QuizDataInput,
  QuizGuardData,
  SleepWithTrackerOption,
} from '@naturalcycles/shared'
import { env } from '@src/environments/environment'
import { QuizBreakpoint, QuizSections } from '@src/typings/quiz.enum'
import { AppQuizFlow, BreakpointOption } from '@src/typings/quiz.model'
import { QuizOverlayConfig } from '@src/typings/quiz-page-config.types'
import { birthControlMethodBreakpoint } from './breakpoints/birth-control-method.breakpoint'
import { conceiveTimingBreakpoint } from './breakpoints/conceive-timing.breakpoint'
import { cycleChangesEmotionalRangeBreakpoint } from './breakpoints/cycle-changes-emotional-range.breakpoint'
import { cycleChangesMucusTypesBreakpoint } from './breakpoints/cycle-changes-mucus-types.breakpoint'
import { cycleChangesPainSymptomsBreakpoint } from './breakpoints/cycle-changes-pain-symptoms.breakpoint'
import { cycleChangesSexDriveBreakpoint } from './breakpoints/cycle-changes-sex-drive.breakpoint'
import { cycleChangesSkinConditionsBreakpoint } from './breakpoints/cycle-changes-skin-conditions.breakpoint'
import { eligibleAppleWatchBreakpoint } from './breakpoints/eligible-apple-watch.breakpoint'
import { fertilityGoalBreakpoint } from './breakpoints/fertility-goal.breakpoint'
import { hormonalBirthControlHowLongBreakpoint } from './breakpoints/hormonal-birth-control-how-long.breakpoint'
import { hormonalBirthControlUsageBreakpoint } from './breakpoints/hormonal-birth-control-usage.breakpoint'
import { medicalConditionsBreakpoint } from './breakpoints/medical-conditions.breakpoint'
import { sideEffectsBreakpoint } from './breakpoints/side-effects.breakpoint'
import { sleepWearableBreakpoint } from './breakpoints/sleep-wearable.breakpoint'
import { whichWearableBreakpoint } from './breakpoints/which-wearable.breakpoint'
import { QuizPage } from './quiz-pages.cnst'

export const quizParentSlug = 'quiz'
export const quizImagesBase = `${env.assetsHost}/images/onboarding-quiz/`

export const allQuizSections: QuizSections = {
  intro: 0,
  goal: 1,
  cycle: 2,
  cycleChanges: 3,
  routine: 4,
}

export const commonQuizFlow: AppQuizFlow = [
  [QuizPage.privacy, QuizPage.name, QuizPage.usps, QuizPage.birthday],
  [QuizPage.fertilityGoal, QuizPage.fertilityGoalInfo],
]

export const sectionRoutine = [
  QuizPage.naturalMethod,
  QuizPage.yourMeasuringDevice,
  QuizPage.whichWearable,
  QuizPage.howDoYouSleep,
  QuizPage.sleepWearable,
  QuizPage.yourDescription,
  QuizPage.fitResult,
  QuizPage.deviceResult,
]

export const sectionSexQuestions = [
  QuizPage.learnAboutSexInfo,
  QuizPage.bestTimeInCycle,
  QuizPage.knowingYourSexDrive,
  QuizPage.sexDriveInfo,
  QuizPage.sexFrequency,
  QuizPage.knowPregnantDays,
  QuizPage.menstrualCycleInfo,
  QuizPage.testimonialInfo,
]

/**
 * Between these two set of pages below, there are more pages, decided by breakpoint FlowBreakpoint.cycleChanges.
 * Start and end part are joined by default as it is the flow when the user does not select any cycle changes on FlowBreakpoint.cycleChanges.
 * More pages are injected in between if user selects cycle changes on FlowBreakpoint.cycleChanges
 */
export const sectionCycleChangesStart = [
  QuizPage.cycleChangesStart,
  QuizPage.cycleChangesEmotionalRange,
  QuizPage.cycleChangesPainSymptoms,
  QuizPage.cycleChangesSexDrive,
  QuizPage.cycleChangesMucusTypes,
  QuizPage.cycleChangesSkinConditions,
]
export const sectionCycleChangesEnd = [QuizPage.trackingInfo]
// ====================================================================================================================================

export const quizAnimation = trigger('fadeIn', [
  transition(':enter', [style({ opacity: 0 }), animate('200ms', style({ opacity: 1 }))]),
  transition(':leave', [style({ opacity: 1 }), animate('200ms', style({ opacity: 0 }))]),
])

function hasCycleChanges(state: QuizDataInput | null): boolean {
  return (
    !!state?.emotionalRange ||
    !!state?.painSymptoms ||
    !!state?.skinConditions ||
    !!state?.mucusTypes ||
    !!state?.sexDriveChanges
  )
}

export const goalMap: Record<GoalBreakpointOption, Goal> = {
  [GoalBreakpointOption.prevent]: Goal.PREVENT,
  [GoalBreakpointOption.preventNowPlanLater]: Goal.PREVENT,

  [GoalBreakpointOption.undecided]: Goal.PREVENT,
  [GoalBreakpointOption.improveWellbeing]: Goal.PREVENT,
  [GoalBreakpointOption.manageSymptoms]: Goal.PREVENT,
  [GoalBreakpointOption.trackCycle]: Goal.PREVENT,

  [GoalBreakpointOption.plan]: Goal.PLAN,
  [GoalBreakpointOption.trackPregnancy]: Goal.PREGNANT,
  [GoalBreakpointOption.postPartum]: Goal.POSTPARTUM,
}

export const guardQuizData: QuizGuardData<QuizPage, QuizDataInput> = {
  [QuizPage.privacy]: () => true,
  [QuizPage.name]: () => true,
  [QuizPage.usps]: state => {
    return !!state?.name
  },
  [QuizPage.birthday]: state => {
    return !!state?.name
  },
  [QuizPage.fertilityGoal]: state => {
    return !!state?.dateOfBirth
  },
  [QuizPage.fertilityGoalInfo]: state => {
    return !!state?.fertilityGoal
  },
  [QuizPage.birthControlMethod]: state => {
    return state?.fertilityGoal !== GoalBreakpointOption.plan
  },
  [QuizPage.birthControlMethodInfo]: state => {
    return !!state?.birthControlMethod
  },
  [QuizPage.sideEffects]: () => true,
  [QuizPage.sideEffectsInfo]: state => {
    return !!state?.sideEffects
  },
  [QuizPage.plannedPregnancyTiming]: state => {
    return state?.fertilityGoal === GoalBreakpointOption.plan
  },
  [QuizPage.hormonalBirthControlUsage]: state => {
    return !!state?.conceiveTiming
  },
  [QuizPage.hormonalBirthControlHowLongPlan]: state => {
    return state?.hormonalBirthControlUsage === HormonalBirthControlUsageBreakpointOption.lastMonth
  },
  [QuizPage.hormonalBirthControlHowLongPrevent]: state => {
    return !!state?.birthControlMethod
  },
  [QuizPage.plannedPregnancyInfo]: state => {
    // This page appears in many flows. To reduce complexity and increase readability we decided to use the last "common" question as the guard.
    // Discussion here: https://github.com/NaturalCycles/WebSignup/pull/1497/files#r1231149370
    return !!state?.conceiveTiming
  },
  [QuizPage.cycleLength]: state => {
    // This page appears in many flows. To reduce complexity and increase readability we decided to use a simplified logic for the route guard.
    // Discussion here https://github.com/NaturalCycles/WebSignup/pull/1497/files#r1231149370
    return !!state?.birthControlMethod || !!state?.conceiveTiming
  },
  [QuizPage.periodRegularity]: state => {
    return !!state?.avgCycleLength
  },
  [QuizPage.periodRegularityInfo]: state => {
    return (
      !!state?.periodRegularity ||
      state?.hormonalBirthControlHowLong ===
        HormonalBirthControlHowLongBreakpointOption.moreThanAYear
    )
  },
  [QuizPage.cycleStartDate]: state => {
    return !!state?.periodRegularity
  },
  [QuizPage.medicalConditionsPlan]: state => {
    return !!state?.conceiveTiming
  },
  [QuizPage.medicalConditionsPrevent]: state => {
    return (
      (!!state?.periodRegularity && state.fertilityGoal !== GoalBreakpointOption.plan) ||
      state?.hormonalBirthControlHowLong ===
        HormonalBirthControlHowLongBreakpointOption.moreThanAYear
    )
  },
  [QuizPage.medicalConditionsInfo]: state => {
    return !!state?.medicalConditions
  },
  [QuizPage.doctorInvolvement]: state => {
    return !!state?.medicalConditions && state.fertilityGoal === GoalBreakpointOption.plan
  },
  [QuizPage.partnerInvolvement]: state => {
    return !!state?.discussedFertility
  },
  [QuizPage.partnerInvolvementInfo]: state => {
    return !!state?.partnerInvolvement
  },
  [QuizPage.followPregnancyInfo]: state => {
    return !!state?.partnerInvolvement
  },

  [QuizPage.cycleChangesStart]: state => {
    return (
      (state?.fertilityGoal !== GoalBreakpointOption.plan && !!state?.medicalConditions) ||
      (state?.fertilityGoal === GoalBreakpointOption.plan && !!state?.partnerInvolvement)
    )
  },

  [QuizPage.cycleChangesEmotionalRange]: state => {
    return (
      (state?.fertilityGoal !== GoalBreakpointOption.plan && !!state?.medicalConditions) ||
      (state?.fertilityGoal === GoalBreakpointOption.plan && !!state?.partnerInvolvement)
    )
  },
  [QuizPage.cycleChangesEmotionalRangeInfo]: state => !!state?.emotionalRange,
  [QuizPage.cycleChangesPainSymptoms]: state => {
    return (
      (state?.fertilityGoal !== GoalBreakpointOption.plan && !!state?.medicalConditions) ||
      (state?.fertilityGoal === GoalBreakpointOption.plan && !!state?.partnerInvolvement)
    )
  },
  [QuizPage.cycleChangesPainSymptomsInfo]: state => !!state?.painSymptoms,
  [QuizPage.cycleChangesSexDrive]: state => !!state?.painSymptoms,
  [QuizPage.cycleChangesMucusTypes]: state => !!state?.painSymptoms,
  [QuizPage.cycleChangesSexDriveAndMucusInfo]: state =>
    !!state?.mucusTypes || !!state?.sexDriveChanges,
  [QuizPage.cycleChangesSkinConditions]: state => !!state?.mucusTypes || !!state?.sexDriveChanges,
  [QuizPage.cycleChangesSkinInfo]: state => !!state?.skinConditions,
  [QuizPage.trackingInfo]: state => hasCycleChanges(state),
  [QuizPage.naturalMethod]: state => hasCycleChanges(state),
  [QuizPage.yourMeasuringDevice]: state => hasCycleChanges(state),
  [QuizPage.whichWearable]: state => hasCycleChanges(state),
  [QuizPage.consideringPurchase]: state => hasCycleChanges(state),
  [QuizPage.howDoYouSleep]: state => {
    return !!state?.whichWearable
  },
  [QuizPage.sleepWearable]: state => {
    return !!state?.sleep
  },
  [QuizPage.wearYourWatchToBed]: state => {
    return (
      (state?.eligibleAppleWatch === EligibleAppleWatchBreakpointOption.yes ||
        state?.purchaseNewWatch === PurchaseNewWatchOption.yes ||
        state?.consideringPurchase === ConsideringPurchaseOption.yesAppleWatch) &&
      (state?.sleepWithTracker === SleepWithTrackerOption.yesWithRing ||
        state?.sleepWithTracker === SleepWithTrackerOption.neither)
    )
  },
  [QuizPage.yourDescription]: state => {
    return !!state?.sleepWithTracker
  },
  [QuizPage.fitResult]: state => {
    return !!state?.sleepWithTracker
  },
  [QuizPage.deviceResult]: state => {
    return !!state?.whichWearable
  },
}

export const breakpointActions: BreakpointActions<
  QuizBreakpoint,
  BreakpointOption,
  QuizPage,
  QuizDataInput
> = {
  [QuizBreakpoint.goal]: fertilityGoalBreakpoint,
  [QuizBreakpoint.birthControlMethod]: birthControlMethodBreakpoint,
  [QuizBreakpoint.sideEffects]: sideEffectsBreakpoint,
  [QuizBreakpoint.conceiveTiming]: conceiveTimingBreakpoint,
  [QuizBreakpoint.hormonalBirthControlUsage]: hormonalBirthControlUsageBreakpoint,
  [QuizBreakpoint.hormonalBirthControlHowLong]: hormonalBirthControlHowLongBreakpoint,
  [QuizBreakpoint.medicalConditions]: medicalConditionsBreakpoint,
  [QuizBreakpoint.cycleChangesEmotionalRange]: cycleChangesEmotionalRangeBreakpoint,
  [QuizBreakpoint.cycleChangesPainSymptoms]: cycleChangesPainSymptomsBreakpoint,
  [QuizBreakpoint.cycleChangesSexDrive]: cycleChangesSexDriveBreakpoint,
  [QuizBreakpoint.cycleChangesMucusTypes]: cycleChangesMucusTypesBreakpoint,
  [QuizBreakpoint.cycleChangesSkinConditions]: cycleChangesSkinConditionsBreakpoint,
  [QuizBreakpoint.whichWearable]: whichWearableBreakpoint,
  [QuizBreakpoint.eligibleAppleWatch]: eligibleAppleWatchBreakpoint,
  [QuizBreakpoint.sleepWearable]: sleepWearableBreakpoint,
}

export const hormonalBcOptions = [
  BirthControlMethodBreakpointOption.hormonalIUD,
  BirthControlMethodBreakpointOption.implant,
  BirthControlMethodBreakpointOption.nuvaRing,
  BirthControlMethodBreakpointOption.patch,
  BirthControlMethodBreakpointOption.pill,
  BirthControlMethodBreakpointOption.shot,
]

export const fertilityGoalImages: Record<GoalBreakpointOption, string> = {
  [GoalBreakpointOption.prevent]: '/assets/img/quiz/prevent.svg',
  [GoalBreakpointOption.plan]: '/assets/img/quiz/plan.svg',
  [GoalBreakpointOption.trackPregnancy]: '/assets/img/quiz/track-pregnancy.svg',
  [GoalBreakpointOption.undecided]: '/assets/img/quiz/havent-decided.svg',
  [GoalBreakpointOption.trackCycle]: 'assets/img/quiz/track-cycle.svg',
  [GoalBreakpointOption.preventNowPlanLater]: 'assets/img/quiz/prevent-now-plan-later.svg',
  [GoalBreakpointOption.improveWellbeing]: 'assets/img/quiz/improve-wellbeing.svg',
  [GoalBreakpointOption.manageSymptoms]: 'assets/img/quiz/manage-symptoms.svg',
  [GoalBreakpointOption.postPartum]: '/assets/img/quiz/postpartum.svg',
}

export function goalSectionOverlay(name: string | undefined): QuizOverlayConfig[] {
  return [
    {
      body: {
        key: 'txt-quiz-thanks-for-sharing',
        params: { name },
      },
      imgSrc: '/assets/img/quiz/heart.png',
      imgAlt: 'Heart',
    },
    {
      body: 'txt-quiz-take-look-your-cycle',
    },
  ]
}
